import { useEffect, useState } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import apiInstance from "../../utils/axios";
import UserData from "../plugins/UserData";
import '../../../css/productName.css';
import { useAuthApp } from '../../app/auth';
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next'; // Import useTranslation
import useAxios from "../../utils/useAxios";

const Toast = Swal.mixin({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

function ProductNameManagement() {
    const [productNames, setProductNames] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [productName, setProductName] = useState("");
    const [options, setOptions] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const { t } = useTranslation();

    const navigate = useNavigate()

    const api = useAxios();

    let lang_browser = navigator.language.toLowerCase();
    let lang = localStorage.getItem('language') || (lang_browser.startsWith("en") ? "en" : lang_browser);

    const isLoggedIn = useAuthApp((state) => state.isLoggedIn)
    const userData = useAuthApp((state) => state.user)

    const userId = userData()?.user_id

    const fetchProductNames = async () => {
        if (!api) return;
        setIsLoading(true);
        try {
            const res = await api.get(`productnames/${userId}/`);
            const fetchedOptions = res.data.map((name) => ({
                value: name.id,
                label: name.name,
            }));
            setOptions(fetchedOptions);
            setProductNames(res.data);
        } catch (error) {
            console.error("Error fetching product names:", error);
            Toast.fire({
                icon: "error",
                title: t("productNameManagement.messages.loginRequired"),
            });
            navigate("/login");
        } finally {
            setIsLoading(false);
        }
    };

     // Update a product name
    const handleUpdate = async (productNameId, updatedName) => {
        if (!api) return;
        try {
        const response = await api.put(`productnames/${userId}/${productNameId}/`, {
            name: updatedName,
        });
        if (response.status === 200) {
            Toast.fire({
            icon: "success",
            title: t("productNameManagement.messages.updateSuccess"),
            });
            fetchProductNames(); // Refresh table
        }
        } catch (error) {
        console.error("Error updating product name:", error);
        Toast.fire({
            icon: "error",
            title: t("productNameManagement.messages.updateError"),
        });
        }
    };

    // const handleDelete = async (productNameId, productName) => {
    //     const result = await Swal.fire({
    //         title: `${t("productNameManagement.messages.deleteConfirmationTitleProductName")}: ${productName}`,
    //         text: t("productNameManagement.messages.deleteConfirmation"),
    //         icon: "warning",
    //         showCancelButton: true,
    //         confirmButtonColor: "#d33",
    //         cancelButtonColor: "#3085d6",
    //         confirmButtonText: t("productNameManagement.buttons.delete"),
    //         cancelButtonText: t("productNameManagement.buttons.cancel"),
    //         customClass: {
    //             popup: "custom-popup-class",
    //             confirmButton: "custom-confirm-button-class",
    //             cancelButton: "custom-cancel-button-class",
    //         },
    //     });
    
    //     if (result.isConfirmed) {
    //         try {
    //             const response = await apiInstance.delete(`productnames/${userId}/${productNameId}/`);
    //             if (response.status === 204) {
    //                 Toast.fire({
    //                     icon: "success",
    //                     title: t("productNameManagement.messages.deleteSuccess"),
    //                 });
    //                 setProductNames(prev => prev.filter(prod => prod.id !== productNameId)); // Remove from state directly
    //             }
    //         } catch (error) {
    //             console.error("Error deleting product:", error);
    //             Toast.fire({
    //                 icon: "error",
    //                 title: t("productNameManagement.messages.deleteError"),
    //             });
    //         }
    //     }
    // };

    // ...existing code...
    const handleDelete = async (productNameId, productName) => {
        if (!api) return;
        const { value: typed } = await Swal.fire({
            title: `${t("productNameManagement.messages.deleteConfirmationTitleProductName")}: ${productName}`,
            html: `
                <p>${t("productNameManagement.messages.deleteConfirmation")}</p>
                <input id="swal-input-confirm" class="swal2-input" placeholder="${t("productNameManagement.labels.typeConfirmToDelete") || "Type 'confirm' to enable delete"}" />`,
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: t("productNameManagement.buttons.delete"),
            cancelButtonText: t("productNameManagement.buttons.cancel"),
            customClass: {
                popup: "custom-popup-class",
                confirmButton: "custom-confirm-button-class",
                cancelButton: "custom-cancel-button-class",
            },
            didOpen: () => {
                const popup = Swal.getPopup();
                if (!popup) return;
                const input = popup.querySelector('#swal-input-confirm');
                const confirmBtn = Swal.getConfirmButton();
                confirmBtn.disabled = true;
                input.addEventListener('input', () => {
                    const value = input.value.trim().toLowerCase();
                    confirmBtn.disabled = (value !== 'delete' && value !== 'löschen');
                });
                input.focus();
            },
            preConfirm: () => {
                const popup = Swal.getPopup();
                const input = popup ? popup.querySelector('#swal-input-confirm') : null;
                const value = input ? input.value.trim().toLowerCase() : '';

                if (value !== 'delete' && value !== 'löschen') {
                    Swal.showValidationMessage(t("productNameManagement.labels.typeConfirmToDelete") || 'Type "confirm" to delete');
                    return false;
                }
                return input.value;
            }
        });

        if (!typed) return; // user cancelled or didn't type confirm

        try {
            const response = await api.delete(`productnames/${userId}/${productNameId}/`);
            if (response.status === 204) {
                Toast.fire({
                    icon: "success",
                    title: t("productNameManagement.messages.deleteSuccess"),
                });
                setProductNames(prev => prev.filter(prod => prod.id !== productNameId));
            }
        } catch (error) {
            console.error("Error deleting product:", error);
            Toast.fire({
                icon: "error",
                title: t("productNameManagement.messages.deleteError"),
            });
        }
    };


    useEffect(() => {
        if (api) {fetchProductNames();}
    }, [api]);

    const handleChange = (e) => setProductName(e.target.value);

    const handleSubmit = async (e) => {
        if (!api) return;
        e.preventDefault();
        setIsLoading(true);
        try {
            const formdata = new FormData();
            formdata.append("name", productName);
            const response = await api.post(`productnames/${userId}/`, formdata);

            if (response.status === 201) {
                await fetchProductNames();
                Toast.fire({
                    icon: "success",
                    title: t("productNameManagement.messages.createSuccess"),
                });
                setProductName(""); // Reset input field
            }
        } catch (error) {
            console.error("Error creating product name:", error);
            Toast.fire({
                icon: "error",
                title: t("productNameManagement.messages.createError"),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async (productNameId) => {
        if (!api) return;
        try {
            const response = await api.get(`hash-id/${productNameId}/`);
            const hashedId = response.data.hashed_id; // Correctly get the decoded ID
    
            navigate(`/create-product/${hashedId}`); // Use the decoded ID
        } catch (error) {
            console.error("Error decoding ID:", error);
        }
    }

      // Filtered product names based on the search term
    const filteredProductNames = productNames.filter((product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div>
            <div className="container-1">
                <div>
                    <h2>{t("productNameManagement.headings.title")}</h2>
                    <div className='form-input-container-2  m-t2r-b2r-l2r'>
                        <label className="form-label-2" htmlFor="product-name-search">{t("productNameManagement.labels.searchProductName")}</label>
                        <input
                            type="text"
                            name="product-name-search"
                            id="product-name-search"
                            className="form-input-1 w25 mw200p"
                            // placeholder="Search by product name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className='form-input-container-hover m-t2r-b2r-l2r'>
                            <label className="form-label-2" htmlFor="product-name-input">{t("productNameManagement.labels.enterNewProductName")}</label>
                            <input
                                id="product-name-input"
                                onChange={handleChange}
                                type="text"
                                name="name"
                                value={productName}
                                // placeholder="Enter product name"
                                className="form-input-1 w25 mw200p" 
                            />
                        
                            <button
                                type="submit"
                                disabled={isLoading}
                                aria-busy={isLoading}
                                className="form-button-submit-productnames"
                            >
                                {isLoading ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i>...
                                    </>
                                ) : (
                                    t("productNameManagement.buttons.save")
                                )}
                            </button>
                        </div>
                    
                    </form>
                    {isLoading ? (
                        <div style={{ textAlign: "center", margin: "2rem" }}>
                            <i className="fas fa-spinner fa-spin" style={{ fontSize: "2rem" }}></i>
                        </div>
                    ) : (
                        productNames.length > 0 ? (
                            <div className="table-container">
                                <table className="productname-table">
                                    <thead>
                                    <tr>
                                        <th className="col-fixed">{t("productNameManagement.tableHeaders.productName")}</th>
                                        <th>{t("productNameManagement.tableHeaders.updateName")}</th>
                                        <th>{t("productNameManagement.tableHeaders.update")}</th>
                                        <th>{t("productNameManagement.tableHeaders.delete")}</th>
                                        <th>{t("productNameManagement.tableHeaders.createProduct")}</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredProductNames.map((productName) => (
                                        <tr key={productName.id}>
                                        <td className="col-fixed">{productName.name}</td>
                                        <td>
                                            <input
                                            type="text"
                                            defaultValue={productName.name}
                                            id={`update-input-${productName.id}`}
                                            className="form-input-1 mw200p"
                                            />
                                        </td>
                                        <td>
                                            <button
                                            onClick={() =>
                                                handleUpdate(
                                                productName.id,
                                                document.getElementById(`update-input-${productName.id}`)
                                                    .value
                                                )
                                            }
                                            className="btn-table-1 btn-u-c"
                                            >
                                            {t("productNameManagement.buttons.update")}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                            onClick={() => handleDelete(productName.id, productName.name)}
                                            className="btn-table-1 btn-d-c"
                                            >
                                            {t("productNameManagement.buttons.delete")}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                            className="btn-table-1 btn-c-c"
                                            onClick={() => handleCreate(productName.id)}

                                            >
                                            {t("productNameManagement.buttons.create")}
                                            </button>
                                        </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                    ) : (
                        <p className="no-products-message">{t("productNameManagement.messages.noProductNames")}</p>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default ProductNameManagement;
